/**
 * AdminMapSettings - Admin panel for controlling map feature settings per country
 * Includes hover-to-zoom toggle and configuration
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { Slider } from './ui/slider';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { 
  Map, MousePointer2, Timer, Move, Save, 
  Loader2, CheckCircle, AlertCircle, RefreshCw
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COUNTRIES = [
  { code: 'UK', name: 'United Kingdom', flag: '🇬🇧' },
  { code: 'LK', name: 'Sri Lanka', flag: '🇱🇰' },
];

export default function AdminMapSettings() {
  const { toast } = useToast();
  const [selectedCountry, setSelectedCountry] = useState('UK');
  const [settings, setSettings] = useState({
    enabled: true,
    zoomLevel: 15,
    animationDuration: 500,
    enablePan: true
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Fetch settings for selected country
  useEffect(() => {
    const fetchSettings = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API_URL}/api/notifications/map-features/${selectedCountry}`);
        if (response.data) {
          setSettings({
            enabled: response.data.enabled ?? true,
            zoomLevel: response.data.settings?.zoom_level ?? 15,
            animationDuration: response.data.settings?.animation_duration ?? 500,
            enablePan: response.data.settings?.enable_pan ?? true
          });
        }
      } catch (err) {
        console.error('Failed to fetch map settings:', err);
        toast({
          title: 'Error',
          description: 'Failed to load map settings',
          variant: 'destructive'
        });
      } finally {
        setLoading(false);
      }
    };
    
    fetchSettings();
  }, [selectedCountry, toast]);

  // Save settings
  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/notifications/map-features/${selectedCountry}`, null, {
        params: {
          enabled: settings.enabled,
          zoom_level: settings.zoomLevel,
          animation_duration: settings.animationDuration,
          enable_pan: settings.enablePan
        }
      });
      
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      
      toast({
        title: 'Settings saved',
        description: `Hover-to-zoom ${settings.enabled ? 'enabled' : 'disabled'} for ${selectedCountry}`,
      });
    } catch (err) {
      console.error('Failed to save map settings:', err);
      toast({
        title: 'Error',
        description: 'Failed to save map settings',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const countryData = COUNTRIES.find(c => c.code === selectedCountry);

  return (
    <Card data-testid="admin-map-settings">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Map className="w-5 h-5 text-blue-600" />
          Map Feature Settings
        </CardTitle>
        <CardDescription>
          Configure hover-to-zoom behavior for property search maps by country
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Country Selector */}
        <div className="space-y-2">
          <Label>Select Country</Label>
          <Select value={selectedCountry} onValueChange={setSelectedCountry}>
            <SelectTrigger className="w-full" data-testid="country-selector">
              <SelectValue>
                {countryData && (
                  <span className="flex items-center gap-2">
                    <span>{countryData.flag}</span>
                    <span>{countryData.name}</span>
                  </span>
                )}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {COUNTRIES.map(country => (
                <SelectItem key={country.code} value={country.code}>
                  <span className="flex items-center gap-2">
                    <span>{country.flag}</span>
                    <span>{country.name}</span>
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
          </div>
        ) : (
          <>
            {/* Hover-to-Zoom Toggle */}
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-3">
                <MousePointer2 className="w-5 h-5 text-gray-600" />
                <div>
                  <Label className="text-base font-medium">Hover-to-Zoom</Label>
                  <p className="text-sm text-gray-500">
                    Pan and zoom map when hovering over property cards
                  </p>
                </div>
              </div>
              <Switch
                checked={settings.enabled}
                onCheckedChange={(checked) => setSettings(prev => ({ ...prev, enabled: checked }))}
                data-testid="hover-zoom-toggle"
              />
            </div>

            {/* Detailed Settings (shown when enabled) */}
            {settings.enabled && (
              <div className="space-y-6 pt-4 border-t">
                {/* Zoom Level */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="flex items-center gap-2">
                      <Map className="w-4 h-4" />
                      Zoom Level
                    </Label>
                    <Badge variant="secondary">{settings.zoomLevel}</Badge>
                  </div>
                  <Slider
                    value={[settings.zoomLevel]}
                    onValueChange={([value]) => setSettings(prev => ({ ...prev, zoomLevel: value }))}
                    min={10}
                    max={18}
                    step={1}
                    className="w-full"
                    data-testid="zoom-level-slider"
                  />
                  <div className="flex justify-between text-xs text-gray-400">
                    <span>City view (10)</span>
                    <span>Street view (18)</span>
                  </div>
                </div>

                {/* Animation Duration */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label className="flex items-center gap-2">
                      <Timer className="w-4 h-4" />
                      Animation Duration
                    </Label>
                    <Badge variant="secondary">{settings.animationDuration}ms</Badge>
                  </div>
                  <Slider
                    value={[settings.animationDuration]}
                    onValueChange={([value]) => setSettings(prev => ({ ...prev, animationDuration: value }))}
                    min={200}
                    max={1000}
                    step={50}
                    className="w-full"
                    data-testid="animation-duration-slider"
                  />
                  <div className="flex justify-between text-xs text-gray-400">
                    <span>Fast (200ms)</span>
                    <span>Smooth (1000ms)</span>
                  </div>
                </div>

                {/* Enable Pan */}
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Move className="w-4 h-4 text-gray-600" />
                    <div>
                      <Label className="text-sm font-medium">Enable Map Pan</Label>
                      <p className="text-xs text-gray-500">Move map center to property location</p>
                    </div>
                  </div>
                  <Switch
                    checked={settings.enablePan}
                    onCheckedChange={(checked) => setSettings(prev => ({ ...prev, enablePan: checked }))}
                    data-testid="enable-pan-toggle"
                  />
                </div>
              </div>
            )}

            {/* Save Button */}
            <div className="pt-4 flex justify-end">
              <Button 
                onClick={handleSave} 
                disabled={saving}
                className="min-w-[120px]"
                data-testid="save-map-settings-btn"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : saved ? (
                  <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                {saved ? 'Saved!' : 'Save Settings'}
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
